import constants from "../constants";

const { IMAGES, CONTENT_ITEM_TYPES, UA_STANDALONE } = constants;

/**
 * Find the image aspect ratio based on width and height of a thumbnail
 *
 * @param {Number} width - thumbnail width in pixels
 * @param {Number} height - thumbnail height in pixels
 */
export const getImageAspectRatioByDims = (width, height) => {
  const ratio = width / height;
  if (ratio > 1.7 && ratio < 1.8) return IMAGES.ASPECT_RATIOS.DIM_9x16;
  return IMAGES.ASPECT_RATIOS.DIM_2x3;
};

export const getAVSKeyArtImageUrl = (
  extendedMetadata,
  isStandAloneEpisode,
  dlumImage,
  standAloneEpisodeDlumImage,
  sourceImage
) => {
  const { dlum, source } = extendedMetadata;
  if (dlum) {
    if (isStandAloneEpisode && extendedMetadata.dlum[standAloneEpisodeDlumImage]) {
      return dlum[standAloneEpisodeDlumImage];
    }

    if (dlum[dlumImage]) {
      return dlum[dlumImage];
    }
  }

  if (source?.[sourceImage]) {
    return source[sourceImage];
  }

  return "";
};

/**
 * Fetch the keyart Image as needed from the AVS system
 *
 * @param {Object} contentItem  - contentItem Metadata
 * @param {String} aspectRatio - landscape or portrait image type
 * @param {Boolean} isUaSportAsset - checks if it is a UA sport asset.
 * @return {String} - Return  image url
 */
export const getAVSKeyArtImage = (contentItem, aspectRatio, isUaSportAsset = false) => {
  let imageUrl = "",
    dlumImage = "",
    standAloneEpisodeDlumImage = "",
    sourceImage = "";
  const isStandAloneEpisode = contentItem?.extendedMetadata?.dlum?.layoutHint === UA_STANDALONE;

  if (contentItem?.extendedMetadata) {
    if (aspectRatio === IMAGES.ASPECT_RATIOS.DIM_2x3) {
      standAloneEpisodeDlumImage = "image23";
      dlumImage = "iconicImage23";
      sourceImage = "image23";
    } else if (aspectRatio === IMAGES.ASPECT_RATIOS.DIM_9x16) {
      standAloneEpisodeDlumImage = "image23";
      dlumImage = "iconicImage169";
      sourceImage = "image169";
    } else if (aspectRatio === IMAGES.ASPECT_RATIOS.DIM_3x4) {
      standAloneEpisodeDlumImage = "";
      dlumImage = "iconicImage34";
      sourceImage = "image43";
    }
    imageUrl = getAVSKeyArtImageUrl(
      contentItem.extendedMetadata,
      isStandAloneEpisode,
      dlumImage,
      standAloneEpisodeDlumImage,
      sourceImage
    );
  } else if (isUaSportAsset) {
    if (aspectRatio === IMAGES.ASPECT_RATIOS.DIM_9x16)
      imageUrl =
        contentItem?.entityType?.toLowerCase() === CONTENT_ITEM_TYPES.team
          ? contentItem.teamLogoUrl43
          : contentItem.seriesPoster169;
  }

  return imageUrl === "" ? contentItem?.pictureUrl ?? null : imageUrl;
};

const getAVSPosterArtImageUrl = (extendedMetadata, isPosterImageFromSource, dlumImage, sourceImage) => {
  const { dlum, source } = extendedMetadata;

  if ((isPosterImageFromSource || !dlum?.[dlumImage]) && source?.[sourceImage]) {
    return extendedMetadata.source[sourceImage];
  }

  if (dlum?.[dlumImage]) {
    return extendedMetadata.dlum[dlumImage];
  }

  return "";
};

/**
 * Fetch the Poster Image as needed from the AVS system
 *
 * @param {Object} contentItem  - contentItem Metadata
 * @param {String} aspectRatio - landscape or portrait image type
 * @param {boolean} isPosterImageFromSource - if poster images are available in source object
 * @return {String} - Return  image url
 */
export const getAVSPosterArtImage = (
  contentItem,
  aspectRatio,
  isPosterImageFromSource = false,
  seriesRecording,
  isTeamRecording
) => {
  let imageUrl = "",
    dlumImage = "",
    sourceImage = "";

  if (isTeamRecording && seriesRecording?.metadata?.seriesPoster) {
    imageUrl = seriesRecording.metadata.seriesPoster;
  } else if (contentItem?.extendedMetadata) {
    if (aspectRatio === IMAGES.ASPECT_RATIOS.DIM_2x3) {
      dlumImage = sourceImage = "image23";
      sourceImage = "image23";
    } else if (aspectRatio === IMAGES.ASPECT_RATIOS.DIM_9x16) {
      dlumImage = sourceImage = "image169";
      sourceImage = "image169";
    } else if (aspectRatio === IMAGES.ASPECT_RATIOS.DIM_3x4) {
      dlumImage = "image34";
      sourceImage = "image43";
    }
    imageUrl = getAVSPosterArtImageUrl(contentItem.extendedMetadata, isPosterImageFromSource, dlumImage, sourceImage);
  } else if (contentItem?.teamLogoUrl43) {
    imageUrl = contentItem.teamLogoUrl43;
  }

  return imageUrl === "" ? contentItem?.pictureUrl ?? null : imageUrl;
};

/**
 * Handle image loading errors and set a fallback image
 *
 * @param {Object} error - Error event object
 * @param {String} fallbackImage - Fallback image
 */
export const handleImageError = (error, fallbackImage) => {
  error.target.onerror = null;
  error.target.src = fallbackImage;
};
